import React from "react";
import modalState from "../../../state-observables/modal/ModalState";
import styles from "./styles.module.css";

export type ErrorAction = {
  label: string;
  onClick?: () => void | Promise<void>;
  variant?: "primary" | "secondary" | "ghost";
  autoClose?: boolean;
  closeBeforeAction?: boolean;
};

type ErrorDetailModalProps = {
  message: string;
  details?: string;
  troubleshootingSteps?: string[];
  actions?: ErrorAction[];
  supportLink?: string;
  tone?: "info" | "warning";
};

function ErrorDetailModal({
  message,
  details,
  troubleshootingSteps = [],
  actions = [],
  supportLink,
  tone = "warning",
}: ErrorDetailModalProps) {
  const [isActing, setIsActing] = React.useState(false);
  const isInfoTone = tone === "info";

  const resolvedActions =
    actions.length > 0
      ? actions
      : [
        {
          label: "Close",
          variant: "secondary" as const,
        },
      ];

  const handleAction = async (action: ErrorAction) => {
    const shouldClose = action.autoClose !== false;
    const closeBeforeAction = action.closeBeforeAction === true;
    if (closeBeforeAction && shouldClose) {
      modalState.closeModal();
    }
    setIsActing(true);
    try {
      if (action.onClick) {
        await action.onClick();
      }
    } catch (err) {
      console.error("Error CTA handler failed:", err);
    } finally {
      if (closeBeforeAction) {
        // already closed before kicking off action
        return;
      }
      if (shouldClose) {
        modalState.closeModal();
        return;
      }
      setIsActing(false);
    }
  };

  return (
    <div className={styles.modalBody}>
      <div className={`${styles.preamble} ${isInfoTone ? styles.infoTone : styles.warningTone}`}>
        <div className={`${styles.badge} ${isInfoTone ? styles.infoBadge : styles.warningBadge}`}>!</div>
        <div>
          <p className={styles.message}>{message}</p>
          {details && <p className={styles.details}>{details}</p>}
        </div>
      </div>

      {troubleshootingSteps.length > 0 && (
        <div className={styles.troubleshooting}>
          <div className={styles.sectionLabel}>Try this</div>
          <ul>
            {troubleshootingSteps.map((step, idx) => (
              <li key={idx}>{step}</li>
            ))}
          </ul>
        </div>
      )}

      {supportLink && (
        <div className={styles.supportLink}>
          Need more help?{" "}
          <a href={supportLink} target="_blank" rel="noreferrer">
            Contact support
          </a>
        </div>
      )}

      <div className={styles.actionRow}>
        {resolvedActions.map((action, idx) => (
          <button
            key={`${action.label}-${idx}`}
            className={`${styles.cta} ${
              action.variant === "secondary"
                ? styles.secondary
                : action.variant === "ghost"
                  ? styles.ghost
                  : isInfoTone
                    ? styles.primaryInfo
                    : styles.primaryWarning
            }`}
            onClick={() => handleAction(action)}
            disabled={isActing}
          >
            {isActing ? "Working..." : action.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default ErrorDetailModal;
